import { Box, Button, Popover, PopoverBody, PopoverContent, PopoverTrigger, Text, VStack } from "@chakra-ui/react";
import { MultiValue, Select as ChakraReactSelect } from "chakra-react-select";
import React, { useEffect, useState } from "react";

// Types
import { KVP } from "../../utils/types";

// API
import { listMatterBillingCodes } from "../../utils/api/benefits";

interface BenefitOption {
	label: string;
	value: string;
}

const SELECTED_BENEFITS_STORAGE_KEY = "advancedFilters.selectedBenefits";

interface AdvancedFiltersPopoverProps {
	onFilters: (benefitCodes: Array<string>) => void;
}

const AdvancedFiltersPopover: React.FC<AdvancedFiltersPopoverProps> = ({ onFilters }) => {
	const [benefits, setBenefits] = useState<Array<KVP>>([]);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [appliedBenefits, setAppliedBenefits] = useState<Array<string>>(() => {
		const storedBenefits = localStorage.getItem(SELECTED_BENEFITS_STORAGE_KEY);

		if (!storedBenefits) return [];

		try {
			const parsedBenefits: unknown = JSON.parse(storedBenefits);
			return Array.isArray(parsedBenefits) && parsedBenefits.every((benefit) => typeof benefit === "string")
				? parsedBenefits
				: [];
		} catch {
			return [];
		}
	});
	const [selectedBenefits, setSelectedBenefits] = useState<Array<string>>(appliedBenefits);

	useEffect(() => {
		if (isOpen) setSelectedBenefits(appliedBenefits);
	}, [appliedBenefits, isOpen]);

	const benefitOptions: Array<BenefitOption> = benefits.map((benefit) => ({
		label: `${benefit.key} ${benefit.value}`,
		value: benefit.key.toString()
	}));

	//Get List Of Claim Benefits
	useEffect(() => {
		const fetchBenefits = async () => {
			const benefitsList = await listMatterBillingCodes();

			const formattedBenefits = benefitsList.getMatterBillingCodesList().map((benefit) => ({
				key: benefit.getMatterBillingCode().toString(),
				value: benefit.getMatterTitle()
			}));

			setBenefits(formattedBenefits);
		};
		fetchBenefits();
	}, []);

	const handleApplyFilters = (): void => {
		setAppliedBenefits(selectedBenefits);
		localStorage.setItem(SELECTED_BENEFITS_STORAGE_KEY, JSON.stringify(selectedBenefits));
		onFilters(selectedBenefits);
		setIsOpen(false);
	};

	const handleClearFilters = (): void => {
		setSelectedBenefits([]);
		setAppliedBenefits([]);
		localStorage.removeItem(SELECTED_BENEFITS_STORAGE_KEY);
		onFilters([]);
		setIsOpen(false);
	};

	return (
		<Popover
			isOpen={isOpen}
			onOpen={() => setIsOpen(true)}
			onClose={() => setIsOpen(false)}
			placement="bottom-end"
			closeOnBlur={false}>
			<PopoverTrigger>
				<Button
					colorScheme="orange"
					variant="solid"
					size="sm"
					aria-label="Additional Filters"
					rightIcon={<Text as="span">⌄</Text>}>
					Additional Filters
				</Button>
			</PopoverTrigger>
			<PopoverContent width={{ base: "calc(100vw - 32px)", md: "680px" }}>
				<PopoverBody>
					<VStack spacing={4} align="stretch">
						<Box>
							<Text mb={2}>Benefits</Text>
							<ChakraReactSelect
								options={benefitOptions}
								value={benefitOptions.filter((benefit) => selectedBenefits.includes(benefit.value))}
								placeholder="Filter Benefits"
								isMulti
								closeMenuOnSelect={false}
								menuPosition="fixed"
								menuPortalTarget={document.body}
								menuShouldBlockScroll
								maxMenuHeight={480}
								onChange={(selectedOptions: MultiValue<BenefitOption>) =>
									setSelectedBenefits(selectedOptions.map((benefit) => benefit.value))
								}
								styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
							/>
						</Box>
						<Box display="flex" justifyContent="center" gap={4}>
							<Button size="sm" variant="outline" onClick={handleClearFilters}>
								Clear
							</Button>
							<Button size="sm" colorScheme="orange" onClick={handleApplyFilters}>
								Filter
							</Button>
						</Box>
					</VStack>
				</PopoverBody>
			</PopoverContent>
		</Popover>
	);
};

export default AdvancedFiltersPopover;
